import React, {createContext, useContext, useMemo} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {usePropsSelector} from '../../state/hooks';
import {Ref as PanelRef} from '../../state/views/panel/types';
import {makePanelRefsSelector} from '../../state/views/panelBankConfig/selectors';
import type {Ref as PanelBankSectionConfigRef} from '../../state/views/panelBankSectionConfig/types';
import {LayedOutPanel} from '../../util/panelTypes';
import {getTrimmedTitleFromAnyConfig} from '../PanelTitle/getTitleFromConfig';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {useDetectPanelVariant} from './hooks';

export const usePanelRefAndPanelBankSectionConfigRef = (panelId: string) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const panelRefAndPanelBankSectionConfigRef = usePropsSelector(
    makePanelRefsSelector,
    panelBankConfigRef,
    {panelId}
  );
  return panelRefAndPanelBankSectionConfigRef;
};

export const usePanelRefAndPanelBankSectionConfigRefByName = (
  panelDisplayName: string,
  panelSectionName?: string
) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const panelRefAndPanelBankSectionConfigRef = usePropsSelector(
    makePanelRefsSelector,
    panelBankConfigRef,
    {
      displayName: panelDisplayName,
      sectionName: panelSectionName,
    }
  );
  return panelRefAndPanelBankSectionConfigRef;
};

const getPanelLinkPathname = () => window.location.pathname.replace(/\/+$/, ''); // strip trailing slashes
const getPanelLinkSearchParams = () =>
  new URLSearchParams(window.location.search);

/**
 * Returns a link to a full-screen panel.
 *
 * If panel.isAuto (was auto-generated and never edited),
 * the URL is based on the panel display name. Otherwise, the
 * URL is based on panel ID. Note that panel IDs are NOT stable
 * for auto panels, which is why we can only use ID-based URLs
 * for non-auto panels.
 */
export const getSharePanelLink = (
  panel: LayedOutPanel,
  panelBankSectionName: string
) => {
  const pathname = getPanelLinkPathname();
  const searchParams = getPanelLinkSearchParams();
  const panelType = panel.viewType;
  let newUrl = '';
  const panelDisplayName = getTrimmedTitleFromAnyConfig(panelType, panel);
  if (panel.isAuto && panelDisplayName) {
    searchParams.set('panelDisplayName', panelDisplayName);
    searchParams.set('panelSectionName', panelBankSectionName);
    newUrl = `${pathname}?${searchParams.toString()}`;
  } else {
    newUrl = getPanelLinkById(panel.__id__);
  }
  return newUrl;
};

/**
 * Returns an ID-based URL for a full-screen panel.
 * PLEASE NOTE THAT ID-BASED URLS SHOULD ONLY BE USED FOR NON-AUTO PANELS.
 */
export const getPanelLinkById = (panelId: string) => {
  const pathname = getPanelLinkPathname();
  const searchParams = getPanelLinkSearchParams();
  // if we're currently on a displayName-based URL, remove
  // those search params since they're no longer needed
  if (searchParams.get('panelDisplayName')) {
    searchParams.delete('panelDisplayName');
  }
  if (searchParams.get('panelSectionName')) {
    searchParams.delete('panelSectionName');
  }
  return `${pathname}/panel/${panelId}?${searchParams.toString()}`;
};

type StandalonePanelContextProviderProps = {
  children: React.ReactNode;
};

type StandalonePanelContextType = {
  panelRefAndPanelBankSectionConfigRef: {
    panelRef: PanelRef;
    panelBankSectionConfigRef: PanelBankSectionConfigRef;
  } | null;
  isPanelNotFound: boolean;
};

const StandalonePanelContext = createContext<
  StandalonePanelContextType | undefined
>(undefined);

export const StandalonePanelContextProvider = ({
  children,
}: StandalonePanelContextProviderProps) => {
  const location = useLocation();
  const {
    isFullScreenPanelById,
    isFullScreenPanelByName,
    isFullScreenPanelByNameAndSectionName,
  } = useDetectPanelVariant();
  const {panelId} = useParams<{panelId: string}>();
  const searchParams = new URLSearchParams(location.search);
  const panelDisplayName = searchParams.get('panelDisplayName');
  const panelSectionName = searchParams.get('panelSectionName');

  const panelRefAndPanelBankSectionConfigRefFromPanelId =
    usePanelRefAndPanelBankSectionConfigRef(panelId ?? '');

  const panelRefAndPanelBankSectionConfigRefFromDisplayName =
    usePanelRefAndPanelBankSectionConfigRefByName(
      panelDisplayName ?? '',
      panelSectionName ?? ''
    );

  let panelRefAndPanelBankSectionConfigRef: {
    panelRef: PanelRef;
    panelBankSectionConfigRef: PanelBankSectionConfigRef;
  } | null = null;
  if (isFullScreenPanelById) {
    panelRefAndPanelBankSectionConfigRef =
      panelRefAndPanelBankSectionConfigRefFromPanelId;
  }
  if (isFullScreenPanelByName || isFullScreenPanelByNameAndSectionName) {
    panelRefAndPanelBankSectionConfigRef =
      panelRefAndPanelBankSectionConfigRefFromDisplayName;
  }

  const value = useMemo(
    () => ({
      panelRefAndPanelBankSectionConfigRef,
      isPanelNotFound: !panelRefAndPanelBankSectionConfigRef,
    }),
    [panelRefAndPanelBankSectionConfigRef]
  );

  return (
    <StandalonePanelContext.Provider value={value}>
      {children}
    </StandalonePanelContext.Provider>
  );
};

export const useStandalonePanelContext = () => {
  const value = useContext(StandalonePanelContext);

  if (value == null) {
    throw new Error(
      'useFullScreenPanelNotFound must be used within a FullScreenPanelNotFound'
    );
  }

  return value;
};
